/*
 * This is a new, custom hook for interacting with Axios for Get requests.
 * It is used as a replacement for feathers, as information on Axios seems to have more information available.
 */

import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "../services/authService";

const useAxios = <T>(
    url: string,
    query?: any,
    autoRefreshPeriod?: number
): [T, boolean, boolean, Error] => {
    //store the state for data, errors, and whether we're currently loading data or not
    const [data, setData] = useState<T>({} as T);
    const [error, setError] = useState(null as any);
    const [loading, setLoading] = useState(true);
    const [longLoading, setLongLoading] = useState(false);
    const lastRequestRef = useRef(0);
    const loadingRef = useRef(loading);
    loadingRef.current = loading;
    const [startTime, setStartTime] = useState(new Date());

    const { getAccessToken } = useAuth();

    //actually fetch the data using Axios
  const fetchData = async (setLoadingState: boolean) => {
        if (setLoadingState) { setLoading(true); setStartTime(new Date()); }
        const request = ++lastRequestRef.current;
        const token = await getAccessToken();

        //set up the headers and query string to be sent as part of the request
        const config = {
            headers: {
                Authorization: `Bearer ${token}`
            },
            params: query
        };

        //actually make the request. If the request being made is the same as the last request
        //(i.e. race conditions haven't happened due to async) then we want to set the response data
        await axios.get(url, config)
            .then((response) => { if (request === lastRequestRef.current) { setData(response.data); }})
            .catch((e) => { setError(e); });

        //set that loading has ended
        if (setLoadingState) { setLoading(false); setLongLoading(false); }
    }

    const updateLongLoading = () => {
      if (loadingRef.current) {
        setLongLoading(true);
      }
    }

    //as this function is called every time one of the major components is re-rendered
    //we only want it to be triggered if the url or the query have changed
    useEffect(() => {
      fetchData(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, query]);

    useEffect(() => {
      const timer = setTimeout(() => {
        updateLongLoading();
      }, 300);
      return () => clearTimeout(timer);
    }, [startTime]);

    //otherwise, if the url or query hasn't changed then we just want the autoRefresh
    //to run to retrieve data. If the url or query has changed, we want to reset the autorefresh.
    useEffect(() => {
        if (autoRefreshPeriod != null) {
            const interval = setInterval(() => fetchData(false), autoRefreshPeriod);
            return () => clearInterval(interval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoRefreshPeriod, url, query]);

    return [data, loading, longLoading, error];
}

export default useAxios;